import { ComponentType, LazyExoticComponent, lazy } from 'react';

/**
 * Wraps dynamic React.lazy imports with automatic retry and reload logic
 * to handle stale webpack chunks after hot-reloads in dev or new deployments in production.
 */
export type PreloadableComponent<T extends ComponentType<any>> = LazyExoticComponent<T> & {
  preload: () => Promise<{ default: T }>;
};

/**
 * Wraps dynamic React.lazy imports with automatic retry and reload logic
 * to handle stale webpack chunks after hot-reloads in dev or new deployments in production.
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
  key = 'route'
): PreloadableComponent<T> {
  const component = lazy(async () => {
    const storageKey = `ns_chunk_reload_${key}`;
    const hasReloaded = typeof window !== 'undefined' ? window.sessionStorage.getItem(storageKey) : null;

    try {
      const result = await factory();
      if (typeof window !== 'undefined') {
        window.sessionStorage.removeItem(storageKey);
      }
      return result;
    } catch (error: any) {
      const isChunkLoadError =
        error?.name === 'ChunkLoadError' ||
        /Loading chunk [\w-]+ failed/i.test(error?.message || '') ||
        /Failed to fetch dynamically imported module/i.test(error?.message || '') ||
        /Importing a module script failed/i.test(error?.message || '');

      if (isChunkLoadError && typeof window !== 'undefined' && !hasReloaded) {
        window.sessionStorage.setItem(storageKey, 'true');
        window.location.reload();
        // Return a pending promise while the browser performs the full reload
        return new Promise<{ default: T }>(() => {});
      }

      // Retry once after a brief delay
      try {
        await new Promise((resolve) => setTimeout(resolve, 800));
        const retryComponent = await factory();
        if (typeof window !== 'undefined') {
          window.sessionStorage.removeItem(storageKey);
        }
        return retryComponent;
      } catch (retryError) {
        throw retryError;
      }
    }
  }) as PreloadableComponent<T>;

  component.preload = factory;
  return component;
}
